/**
 * v3.18-1308 — Lender Portal Credentials Section
 *
 * Internal-only operational tool used by funding staff to log into
 * third-party lender portals on behalf of clients while working
 * financing offers.  Rendered directly beneath Offer #2 inside
 * ``DealDashboard`` and directly above the disposition checkboxes.
 *
 * Access control: the component renders NOTHING at all — no wrapper,
 * no placeholder, no disabled controls — for unauthorized companies.
 * Authorization is decided by a preflight call to
 * ``GET /api/lender-portal/access`` (backed by
 * ``services/lender_portal_access.py``).  Every backend endpoint
 * additionally enforces the same rule with HTTP 403, so hiding the UI
 * is defence-in-depth, never the whole story.
 *
 * Password handling:
 *   • Passwords are Fernet-encrypted at rest.
 *   • The reveal action requires an explicit click, POSTs to the
 *     ``/reveal`` endpoint, and auto-hides after 30 seconds.
 *   • Copy Username / Copy Password / Launch Portal actions each
 *     POST an audit event so those UI actions land in system_audit.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Copy, Eye, EyeOff, ExternalLink, KeyRound, Loader2, Plus, Save, Trash2, X,
} from 'lucide-react';

const REVEAL_TIMEOUT_MS = 30_000;

const emptyDraft = { portal_url: '', client_username: '', client_password: '', lender_name: '' };

function OfferPanel({ clientId, offerIndex, offerLabel }) {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [editing,  setEditing]  = useState(null);       // credential id being edited, or 'new'
  const [draft,    setDraft]    = useState(emptyDraft);
  const [revealed, setRevealed] = useState({});         // {credId: plaintext}
  const revealTimers = useRef({});

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/lender-portal/credentials', {
        params: { client_id: clientId, offer_index: offerIndex },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load lender portal credentials.');
    } finally {
      setLoading(false);
    }
  }, [clientId, offerIndex]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Clear any pending reveal timer on unmount.
  useEffect(() => () => {
    Object.values(revealTimers.current).forEach(clearTimeout);
  }, []);

  const openNew = () => { setEditing('new'); setDraft(emptyDraft); };
  const openEdit = (r) => {
    setEditing(r.id);
    setDraft({
      portal_url:      r.portal_url || '',
      client_username: r.client_username || '',
      client_password: '',            // never prefilled; blank = keep existing
      lender_name:     r.lender_name || '',
    });
  };
  const cancel = () => { setEditing(null); setDraft(emptyDraft); };

  const save = async () => {
    if (!draft.portal_url.trim() || !draft.client_username.trim()) {
      toast.error('Portal URL and Client Username are required.');
      return;
    }
    if (editing === 'new' && !draft.client_password) {
      toast.error('Client Password is required for a new credential.');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/lender-portal/credentials', {
          client_id:       clientId,
          offer_index:     offerIndex,
          portal_url:      draft.portal_url.trim(),
          client_username: draft.client_username.trim(),
          client_password: draft.client_password,
          lender_name:     draft.lender_name.trim() || null,
        });
        toast.success('Credential saved.');
      } else {
        const patch = {
          portal_url:      draft.portal_url.trim(),
          client_username: draft.client_username.trim(),
          lender_name:     draft.lender_name.trim() || null,
        };
        if (draft.client_password) patch.client_password = draft.client_password;
        await api.patch(`/lender-portal/credentials/${editing}`, patch);
        toast.success('Credential updated.');
      }
      cancel();
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the ${r.lender_name || 'lender'} portal credential? This is audited.`)) return;
    try {
      await api.delete(`/lender-portal/credentials/${r.id}`);
      toast.success('Credential deleted.');
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const reveal = async (r) => {
    if (revealed[r.id]) {
      // Already revealed — toggle off.
      setRevealed((prev) => { const n = { ...prev }; delete n[r.id]; return n; });
      clearTimeout(revealTimers.current[r.id]);
      delete revealTimers.current[r.id];
      return;
    }
    try {
      const { data } = await api.post(`/lender-portal/credentials/${r.id}/reveal`);
      setRevealed((prev) => ({ ...prev, [r.id]: data.password || '' }));
      revealTimers.current[r.id] = setTimeout(() => {
        setRevealed((prev) => { const n = { ...prev }; delete n[r.id]; return n; });
        delete revealTimers.current[r.id];
      }, REVEAL_TIMEOUT_MS);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reveal failed.');
    }
  };

  const audit = async (r, action) => {
    try { await api.post(`/lender-portal/credentials/${r.id}/audit`, { action }); }
    catch (_) { /* audit failure must not block the user action */ }
  };

  const copyUsername = async (r) => {
    try {
      await navigator.clipboard.writeText(r.client_username || '');
      audit(r, 'username_copied');
      toast.success('Username copied.');
    } catch (_) { toast.error('Clipboard copy failed.'); }
  };

  const copyPassword = async (r) => {
    try {
      // Always fetch fresh plaintext from the reveal endpoint so the
      // frontend never has to cache credentials.  Reveal already
      // writes an audit row; add a second row for the copy action.
      let plain = revealed[r.id];
      if (!plain) {
        const { data } = await api.post(`/lender-portal/credentials/${r.id}/reveal`);
        plain = data.password || '';
      }
      await navigator.clipboard.writeText(plain);
      audit(r, 'password_copied');
      toast.success('Password copied.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Clipboard copy failed.'); }
  };

  const launch = async (r) => {
    if (!r.portal_url) return;
    audit(r, 'portal_launched');
    // Never carry credentials in the URL — just navigate to the stored URL.
    window.open(r.portal_url, '_blank', 'noopener,noreferrer');
  };

  const hasRow = items.length > 0;

  return (
    <div className="border border-slate-200 rounded-lg bg-white p-4 space-y-3"
         data-testid={`lender-portal-offer-${offerIndex}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-slate-600" />
          <div className="text-sm font-semibold text-slate-800">{offerLabel} Lender Portal</div>
        </div>
        {!hasRow && editing !== 'new' && (
          <Button size="sm" variant="outline" onClick={openNew}
                  data-testid={`lender-portal-add-btn-${offerIndex}`}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add credentials
          </Button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}

      {!loading && !hasRow && editing !== 'new' && (
        <div className="text-xs text-slate-500 italic">
          No portal credentials stored yet for {offerLabel}. Click <b>Add credentials</b> if
          this lender issues a client portal login.
        </div>
      )}

      {editing === 'new' && (
        <CredentialEditor
          offerIndex={offerIndex}
          isNew
          draft={draft}
          setDraft={setDraft}
          onSave={save}
          onCancel={cancel}
          saving={saving}
        />
      )}

      {!loading && hasRow && items.map((r) => (
        <div key={r.id} className="border border-slate-100 rounded p-3 bg-slate-50/50 space-y-2"
             data-testid={`lender-portal-row-${r.id}`}>
          {editing === r.id ? (
            <CredentialEditor
              offerIndex={offerIndex}
              draft={draft}
              setDraft={setDraft}
              onSave={save}
              onCancel={cancel}
              saving={saving}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <ReadonlyField label="Portal URL" value={r.portal_url} testId={`lender-portal-url-${r.id}`} />
                <ReadonlyField label="Client Username" value={r.client_username} testId={`lender-portal-username-${r.id}`} />
                <ReadonlyField
                  label="Client Password"
                  value={revealed[r.id] ?? (r.has_password ? '••••••••••••' : '(not set)')}
                  testId={`lender-portal-password-${r.id}`}
                  isPassword
                />
              </div>
              {r.lender_name && (
                <div className="text-[11px] text-slate-500">Lender: {r.lender_name}</div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="default" onClick={() => launch(r)}
                        data-testid={`lender-portal-launch-${r.id}`}>
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Launch Portal
                </Button>
                <Button size="sm" variant="outline" onClick={() => copyUsername(r)}
                        data-testid={`lender-portal-copy-user-${r.id}`}>
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy Username
                </Button>
                <Button size="sm" variant="outline" onClick={() => copyPassword(r)}
                        data-testid={`lender-portal-copy-pw-${r.id}`}>
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy Password
                </Button>
                <Button size="sm" variant="outline" onClick={() => reveal(r)}
                        data-testid={`lender-portal-reveal-${r.id}`}>
                  {revealed[r.id] ? <><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide Password</>
                                  : <><Eye    className="h-3.5 w-3.5 mr-1" /> Reveal Password</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                        data-testid={`lender-portal-edit-${r.id}`}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(r)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`lender-portal-delete-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </div>
              <div className="text-[10px] text-slate-400 border-t border-slate-100 pt-1">
                Created by {r.created_by_name || '—'} · {(r.created_at || '').slice(0, 16).replace('T', ' ')}
                {r.updated_at && r.updated_at !== r.created_at && (
                  <> · updated by {r.updated_by_name || '—'} · {(r.updated_at || '').slice(0, 16).replace('T', ' ')}</>
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function ReadonlyField({ label, value, testId, isPassword }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">{label}</div>
      <div
        className={`font-mono text-xs break-all ${isPassword ? 'text-slate-800' : 'text-slate-700'}`}
        data-testid={testId}
      >
        {value || <span className="italic text-slate-400">(not set)</span>}
      </div>
    </div>
  );
}

function CredentialEditor({ offerIndex, isNew, draft, setDraft, onSave, onCancel, saving }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Portal URL *</Label>
        <Input
          type="url"
          placeholder="https://portal.lender.example/login"
          value={draft.portal_url}
          onChange={(e) => setDraft({ ...draft, portal_url: e.target.value })}
          data-testid={`lender-portal-edit-url-${offerIndex}`}
        />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Client Username *</Label>
        <Input
          value={draft.client_username}
          onChange={(e) => setDraft({ ...draft, client_username: e.target.value })}
          data-testid={`lender-portal-edit-user-${offerIndex}`}
        />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">
          Client Password {isNew ? '*' : '(leave blank to keep)'}
        </Label>
        <Input
          type="password"
          autoComplete="new-password"
          value={draft.client_password}
          onChange={(e) => setDraft({ ...draft, client_password: e.target.value })}
          data-testid={`lender-portal-edit-pw-${offerIndex}`}
        />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Lender (optional)</Label>
        <Input
          value={draft.lender_name}
          onChange={(e) => setDraft({ ...draft, lender_name: e.target.value })}
          placeholder="e.g. Ally, Concora, PTC Finance…"
          data-testid={`lender-portal-edit-lender-${offerIndex}`}
        />
      </div>
      <div className="md:col-span-4 flex gap-2">
        <Button size="sm" onClick={onSave} disabled={saving}
                data-testid={`lender-portal-save-${offerIndex}`}>
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}
                data-testid={`lender-portal-cancel-${offerIndex}`}>
          <X className="h-3.5 w-3.5 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
}

export default function LenderPortalSection({ clientId }) {
  const [allowed, setAllowed] = useState(null);      // null=loading, true/false decided

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/lender-portal/access');
        if (alive) setAllowed(!!data.allowed);
      } catch (_) {
        if (alive) setAllowed(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!allowed) return null;   // hard-hide when denied or still deciding

  return (
    <Card className="border-slate-200" data-testid="lender-portal-section">
      <CardHeader className="py-2 px-4 border-b border-slate-100">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800">
          <KeyRound className="h-4 w-4 text-blue-600" />
          Lender Portal
          <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500 font-normal">
            Internal use only · Encrypted at rest
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <OfferPanel clientId={clientId} offerIndex={1} offerLabel="Offer 1" />
        <OfferPanel clientId={clientId} offerIndex={2} offerLabel="Offer 2" />
      </CardContent>
    </Card>
  );
}
